import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import jobService from '../../services/jobService';
import consultantApi from '../../services/consultantApi';
import api from '../../services/api';
import { validateJobForm, cleanJobFormData, getJobErrorMessage } from '../../utils/jobValidation';
import './PostJob.css';

const PostJob = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [educations, setEducations] = useState([]);
  const [employerSkills, setEmployerSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [benefits, setBenefits] = useState([]);
  const [newBenefit, setNewBenefit] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('self');
  const [customBenefitInput, setCustomBenefitInput] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    responsibilities: '',
    requirements: '',
    benefits: [],
    customBenefits: [], // For custom text benefits
    jobType: '',
    shiftType: '',
    industryType: '',
    educationRequired: '',
    applicationMethod: {
      type: 'internal'
    },
    workMode: 'onsite',
    location: {
      country: '',
      state: '',
      city: '',
      address: ''
    },
    salary: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'monthly',
      negotiable: false
    },
    experienceRequired: {
      min: '',
      max: ''
    },
    requiredSkills: [],
    optionalSkills: [],
    numberOfPositions: 0,
    isFeatured: false,
    isUrgent: false,
    status: 'draft'
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  const handleBenefitsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      benefits: checked
        ? [...prev.benefits, value]
        : prev.benefits.filter(id => id !== value)
    }));
  };

  const handleAddCustomBenefit = () => {
    const trimmed = customBenefitInput.trim();
    if (trimmed && !formData.customBenefits.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customBenefits: [...prev.customBenefits, trimmed]
      }));
      setCustomBenefitInput('');
    }
  };

  const handleRemoveCustomBenefit = (benefitToRemove) => {
    setFormData(prev => ({
      ...prev,
      customBenefits: prev.customBenefits.filter(b => b !== benefitToRemove)
    }));
  };

  const handleAddEmployerSkill = async () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;

    try {
      const res = await api.post('/employer/skills', {
        name: trimmed
      });

      const createdSkill = res.data;

      // Add to employer skills list
      setEmployerSkills(prev => [...prev, createdSkill]);

      // Auto select it
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, createdSkill._id]
      }));

      setNewSkill('');
      toast.success('Skill added successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || 'Failed to add skill');
    }
  };

  const handleRemoveEmployerSkill = async (skillId) => {
    try {
      await api.delete(`/employer/skills/${skillId}`);
      setEmployerSkills(prev => prev.filter(s => s._id !== skillId));

      // Remove from form selection if present
      setFormData(prev => ({
        ...prev,
        requiredSkills: prev.requiredSkills.filter(id => id !== skillId),
        optionalSkills: prev.optionalSkills?.filter(id => id !== skillId) || []
      }));

      toast.success('Skill deleted successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete skill');
    }
  };

  const fetchMasterData = async () => {
    try {
      // Fetch companies based on user role
      let companiesPromise;
      if (user?.role === 'consultant') {
        // For consultants, fetch their associated companies
        companiesPromise = consultantApi.getMyCompanies({ status: 'accepted' });
      } else {
        // For employers, fetch their companies
        companiesPromise = api.get('/employer/company/my-companies');
      }

      const [
        companiesRes,
        jobTypesRes,
        shiftTypesRes,
        industryTypesRes,
        educationsRes,
        employerSkillsRes
      ] = await Promise.all([
        companiesPromise,
        api.get('/master/job-types'),
        api.get('/master/shift-types'),
        api.get('/master/industry-types'),
        api.get('/master/educations'),
        api.get('/employer/skills').catch(() => ({ data: [] })) // Get employer skills
      ]);

      // Handle different response structures for consultant vs employer
      if (user?.role === 'consultant') {
        // Consultant API returns nested: response.data.data grouped by status
        const groupedData = companiesRes.data.data || companiesRes.data;
        const acceptedCompanies = groupedData?.active || [];
        const companyList = acceptedCompanies.map(assoc => ({
          _id: assoc.company?._id || assoc.companyId?._id,
          name: assoc.company?.name || assoc.companyId?.name,
          industry: assoc.company?.industry || assoc.companyId?.industry,
          associationId: assoc.associationId,
        }));
        setCompanies(companyList);
      } else {
        const employerCompanies = companiesRes.data || [];

        if (employerCompanies.length === 0) {
          toast.error('Please create your company profile first before posting jobs.', { toastId: 'employer-company-required' });
          navigate('/employer/profile');
          return;
        }

        setCompanies(employerCompanies);
      }

      setJobTypes(jobTypesRes.data || []);
      setShiftTypes(shiftTypesRes.data || []);
      setIndustryTypes(industryTypesRes.data || []);
      setEducations(educationsRes.data || []);
      setEmployerSkills(employerSkillsRes.data || []);

      // Benefits: Try multiple endpoints to get ALL benefits including admin-added ones
      let allBenefits = [];

      try {
        // First try: Get employer-specific benefits (includes admin + employer's own)
        const employerBenefitsRes = await api.get('/employer/benefits');
        const employerBenefits = employerBenefitsRes.data || [];
        // console.log('Employer benefits loaded:', employerBenefits.length, 'items');
        allBenefits = [...employerBenefits];
      } catch (employerError) {
        // console.log('Employer benefits endpoint failed:', employerError.message);
      }

      try {
        // Second try: Get master benefits (admin-created only)
        const masterBenefitsRes = await api.get('/master/benefits');
        const masterBenefits = masterBenefitsRes.data || [];
        // console.log('Master benefits loaded:', masterBenefits.length, 'items');

        // Merge and deduplicate by _id
        const mergedBenefits = [...allBenefits];
        masterBenefits.forEach(masterBenefit => {
          if (!mergedBenefits.find(b => b._id === masterBenefit._id)) {
            mergedBenefits.push(masterBenefit);
          }
        });
        allBenefits = mergedBenefits;
      } catch (masterError) {
        // console.log('Master benefits endpoint failed:', masterError.message);
      }

      // If we have benefits from API, use them
      if (allBenefits.length > 0) {
        setBenefits(allBenefits);
        // console.log('Total benefits loaded:', allBenefits.length, 'items');
      } else {
        // Final fallback: Use hardcoded list if no API endpoints work
        // console.log('No benefits from API, using fallback list');
        const fallbackBenefits = [
          { _id: '507f1f77bcf86cd799439011', name: 'Health Insurance' },
          { _id: '507f1f77bcf86cd799439015', name: 'Paid Time Off' },
          { _id: '507f1f77bcf86cd799439016', name: 'Sick Leave' },
          { _id: '507f1f77bcf86cd799439017', name: 'Maternity/Paternity Leave' },
          { _id: '507f1f77bcf86cd799439018', name: 'Flexible Work Hours' },
          { _id: '507f1f77bcf86cd79943901c', name: 'Performance Bonuses' },
          { _id: '507f1f77bcf86cd799439024', name: 'Food Provided' }
        ];
        setBenefits(fallbackBenefits);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
      const errorMessage =
        error?.message ||
        error?.error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'Failed to load form data';

      // Redirect employer to company profile setup if company not created
      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('company profile')) {
        toast.error(errorMessage, { toastId: 'employer-company-required' });
        navigate('/employer/profile');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const sanitized = value.replace(/\D/g, '');
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: sanitized === '' ? '' : Number(sanitized)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitized === '' ? '' : Number(sanitized)
      }));
    }
  };

  const handleSkillsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      requiredSkills: checked
        ? [...prev.requiredSkills, value]
        : prev.requiredSkills.filter(id => id !== value)
    }));
  };

  const handleOptionalSkillsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      optionalSkills: checked
        ? [...prev.optionalSkills, value]
        : prev.optionalSkills.filter(id => id !== value)
    }));
  };

  const handleAddBenefit = async () => {
    if (!newBenefit.trim()) return;

    try {
      const res = await api.post('/employer/benefits', {
        name: newBenefit
      });

      const createdBenefit = res.data;

      // console.log(createdBenefit, "createdBenefitcreatedBenefit");

      // Add to benefits list
      setBenefits(prev => [...prev, createdBenefit]);

      // Auto select it
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, createdBenefit._id]
      }));

      setNewBenefit('');
    } catch (error) {
      toast.error(error?.message || 'failed to add benefit');
    }
  };

  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();

    if (formData.experienceRequired.min === '' || formData.experienceRequired.max === '') {
      toast.error('Min and Max Experience are required');
      return;
    }

    if (Number(formData.experienceRequired.max) < Number(formData.experienceRequired.min)) {
      toast.error('Max Experience should be greater than or equal to Min Experience');
      return;
    }

    // Prepare job data
    const chosenCompany = selectedCompanyId !== 'self'
      ? companies.find(c => c._id === selectedCompanyId)
      : null;

    // Handle case where employer has no companies or chooses to post as employer
    const finalCompanyId = chosenCompany?._id || null;
    const isEmployerPosting = !finalCompanyId; // No company means posting as employer

    // console.log('=== EMPLOYER JOB POSTING DEBUG ===');
    // console.log('User role:', user?.role);
    // console.log('Selected company ID:', selectedCompanyId);
    // console.log('Available companies:', companies.length);
    // console.log('Chosen company:', chosenCompany);
    // console.log('Final company ID:', finalCompanyId);
    // console.log('Is employer posting:', isEmployerPosting);
    // console.log('================================');

    const jobData = {
      ...formData,
      status,
      salary: {
        ...formData.salary,
        min: parseInt(formData.salary.min) || 0,
        max: parseInt(formData.salary.max) || 0
      },
      experienceRequired: {
        min: parseInt(formData.experienceRequired.min) || 0,
        max: parseInt(formData.experienceRequired.max) || 0
      },
      numberOfPositions: parseInt(formData.numberOfPositions) || 1,
      companyId: finalCompanyId,
      postUnderOwnName: isEmployerPosting, // true if posting as employer (no company)
      postedByName: isEmployerPosting ? user?.name : undefined,
      industryTypes: chosenCompany?.industry || null
    };

    // console.log('Job data:', jobData);

    // Validate form data
    const validation = validateJobForm(jobData);
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      toast.error(firstError);
      return;
    }

    try {
      setLoading(true);

      // Clean data before submission
      const cleanedData = cleanJobFormData(jobData);

      // Use different API based on user role
      let response;
      // console.log('PostJob - User role:', user?.role);
      // console.log('PostJob - User data:', user);
      // console.log('PostJob - Token:', localStorage.getItem('token'));
      // console.log('PostJob - API URL:', process.env.REACT_APP_API_URL);

      if (user?.role === 'consultant') {
        // console.log('PostJob - Using consultant API');
        response = await consultantApi.createJob(cleanedData);
        toast.success('Job created as draft successfully!');
        navigate('/consultant/jobs');
      } else {
        // console.log('PostJob - Using employer API');
        response = await jobService.createJob(cleanedData);

        // Increment usage count for employer skills used in this job
        const employerSkillIds = formData.requiredSkills.filter(skillId =>
          employerSkills.some(empSkill => empSkill._id === skillId)
        );

        if (employerSkillIds.length > 0) {
          await Promise.all(
            employerSkillIds.map(skillId =>
              api.post(`/employer/skills/${skillId}/use`).catch(err =>
                console.log('Failed to increment skill usage:', err)
              )
            )
          );
        }

        toast.success(status === 'active' ? 'Job published successfully!' : 'Job saved as draft');
        navigate('/employer/my-jobs');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      const errorMessage = getJobErrorMessage(error.error || error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="emp-post-job-container">
      {/* Hero */}
      <div className="emp-post-job-hero">
        <h1>Post a New Job</h1>
        <p>Fill in the details below to create your job posting</p>
      </div>

      <form className="emp-post-job-form">
        {/* Basic Information */}
        <div className="emp-post-job-card">
          <h2 className="emp-post-job-card-title">
            <span className="material-symbols-outlined">info</span>
            Basic Information
          </h2>
          <div className="emp-post-job-form">
            {/* <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Post As / Company <span>*</span></label>
                {user?.role === 'consultant' ? (
                  <select
                    className="emp-post-job-select jl-filter-drop"
                    value={selectedCompanyId}
                    onChange={e => setSelectedCompanyId(e.target.value)}
                  >
                    <option value="self">My Name ({user?.name})</option>
                    {companies.map(c => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={companies[0]?.name || ''} className="emp-post-job-input" disabled />
                )}
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Industry</label>
                {user?.role === 'consultant' ? (
                  <select name="industryType" value={formData.industryType} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                    <option value="">Select Industry</option>
                    {industryTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}
                  </select>
                ) : (
                  <input type="text" value={companies[0]?.industry?.name || ''} className="emp-post-job-input" disabled />
                )}
              </div>
            </div> */}

            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Job Title <span>*</span></label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Job Title" className="emp-post-job-input" required />
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Job Type <span>*</span></label>
                <select name="jobType" value={formData.jobType} onChange={handleChange} className="emp-post-job-select jl-filter-drop" required>
                  <option value="">Select Job Type</option>
                  {jobTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}
                </select>
              </div>
            </div>

            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Work Mode <span>*</span></label>
                <select name="workMode" value={formData.workMode} onChange={handleChange} className="emp-post-job-select jl-filter-drop" required>
                  <option value="onsite">Onsite</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Shift Type</label>
                <select name="shiftType" value={formData.shiftType} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                  <option value="">Select Shift Type</option>
                  {shiftTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}
                </select>
              </div>

            </div>

            <div className="emp-post-job-field" style={{maxWidth:200}}>
              <label className="emp-post-job-label">Number of Positions</label>
              <input type="number" name="numberOfPositions" value={formData.numberOfPositions} onChange={handleChange} min="1" className="emp-post-job-input" />
            </div>
          </div>
        </div>

                {/* Location */}
        <div className="emp-post-job-card">
          <h2 className="emp-post-job-card-title">
            <span className="material-symbols-outlined">location_on</span>
            Location
          </h2>
          <div className="emp-post-job-form">
            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Address</label>
                <input type="text" name="location.address" value={formData.location.address} onChange={handleChange} placeholder="Street address" className="emp-post-job-input" />
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Country</label>
                <input type="text" name="location.country" value={formData.location.country || ''} onChange={handleChange} className="emp-post-job-input" />
              </div>
            </div>
            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">State</label>
                <input type="text" name="location.state" value={formData.location.state || ''} onChange={handleChange} className="emp-post-job-input" />
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">City</label>
                <input type="text" name="location.city" value={formData.location.city || ''} onChange={handleChange} className="emp-post-job-input" />
              </div>
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="emp-post-job-card">
          <h2 className="emp-post-job-card-title">
            <span className="material-symbols-outlined">description</span>
            Job Description
          </h2>
          <div className="emp-post-job-form">
            <div className="emp-post-job-field">
              <label className="emp-post-job-label">Description <span>*</span></label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={5} placeholder="Describe the job role..." className="emp-post-job-textarea" required />
            </div>
            {/* <div className="emp-post-job-field">
              <label className="emp-post-job-label">Responsibilities</label>
              <textarea name="responsibilities" value={formData.responsibilities} onChange={handleChange} rows={5} placeholder="List key responsibilities..." className="emp-post-job-textarea" />
            </div> */}
            <div className="emp-post-job-field">
              <label className="emp-post-job-label">Additional Requirements</label>
              <textarea name="requirements" value={formData.requirements} onChange={handleChange} rows={5} placeholder="List requirements and qualifications..." className="emp-post-job-textarea" />
            </div>

            <div className="emp-post-job-field">
              <label className="emp-post-job-label">Benefits</label>
              <div className="emp-post-job-skills-grid">
                {benefits.map(b => (
                  <label key={b._id} className={`emp-post-job-skill-chip${formData.benefits.includes(b._id) ? ' selected' : ''}`}>
                    <input type="checkbox" value={b._id} checked={formData.benefits.includes(b._id)} onChange={handleBenefitsChange} />
                    {b.name}
                  </label>
                ))}
              </div>

              {/* Custom Benefits Section */}
              {formData.customBenefits.length > 0 && (
                <div style={{marginTop: 15}}>
                  <div style={{fontSize: 14, color: '#101721', marginBottom: 8, fontWeight: 500}}>Custom Benefits:</div>
                  <div className="emp-post-job-skills-grid">
                    {formData.customBenefits.map((customBenefit, index) => (
                      <label key={`custom-${index}`} className="emp-post-job-skill-chip selected">
                        <span>{customBenefit}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomBenefit(customBenefit)}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ×
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="emp-post-job-benefit-row" style={{marginTop:15}}>
                <input
                  type="text"
                  placeholder="Add custom benefit..."
                  value={customBenefitInput}
                  onChange={(e) => setCustomBenefitInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomBenefit()}
                  className="emp-post-job-input emp-post-job-benefit-input"
                />
                <button type="button" onClick={handleAddCustomBenefit} className="emp-post-job-add-btn">
                  <span className="material-symbols-outlined">add</span> Add
                </button>
              </div>
            </div>

            {/* <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Application Deadline</label>
                <input type="date" name="applicationDeadline" value={formData.applicationDeadline} onChange={handleChange} className="emp-post-job-input" />
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Application Method</label>
                <select name="applicationMethod.type" value={formData.applicationMethod?.type} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                  <option value="internal">Apply on Platform</option>
                  <option value="email">Apply via Email</option>
                  <option value="external">Apply via External Link</option>
                </select>
              </div>
            </div> */}

            {/* {formData.applicationMethod.type === 'email' && (
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Application Email</label>
                <input type="email" name="applicationMethod.email" value={formData.applicationMethod.email || ''} onChange={(e) => setFormData({...formData, applicationMethod: {...formData.applicationMethod, email: e.target.value}})} placeholder="hiring@company.com" className="emp-post-job-input" />
              </div>
            )}
            {formData.applicationMethod.type === 'external' && (
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">External Apply URL</label>
                <input type="url" name="applicationMethod.externalUrl" value={formData.applicationMethod.externalUrl || ''} onChange={(e) => setFormData({...formData, applicationMethod: {...formData.applicationMethod, externalUrl: e.target.value}})} placeholder="https://company.com/apply" className="emp-post-job-input" />
              </div>
            )} */}
          </div>
        </div>

        {/* Requirements */}
        <div className="emp-post-job-card">
          <h2 className="emp-post-job-card-title">
            <span className="material-symbols-outlined">psychology</span>
            Skills
          </h2>
          <div className="emp-post-job-form">
            <div className="emp-post-job-field">
              {/* <label className="emp-post-job-label">Required Skills</label> */}

              {/* Employer Custom Skills */}
              {employerSkills.length > 0 && (
                <div style={{marginBottom: 15}}>
                  <div style={{fontSize: 14, color: '#101721', marginBottom: 8, fontWeight: 500}}>Available Skills:</div>
                  <div className="emp-post-job-skills-grid">
                    {employerSkills.map(skill => (
                      <label key={skill._id} className={`emp-post-job-skill-chip${formData.requiredSkills.includes(skill._id) ? ' selected' : ''}`}>
                        <input type="checkbox" value={skill._id} checked={formData.requiredSkills.includes(skill._id)} onChange={handleSkillsChange} />
                        <span>{skill.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveEmployerSkill(skill._id)}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ×
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Skill */}
              <div className="emp-post-job-benefit-row" style={{marginBottom: 15}}>
                <input
                  type="text"
                  placeholder="Add custom skill..."
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmployerSkill()}
                  className="emp-post-job-input emp-post-job-benefit-input"
                />
                <button type="button" onClick={handleAddEmployerSkill} className="emp-post-job-add-btn">
                  <span className="material-symbols-outlined">add</span> Add Skill
                </button>
              </div>

              {/* Master Skills */}
              {/* <div style={{marginTop: 15}}>
                <div style={{fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600}}>Available Skills:</div>
                <div className="emp-post-job-skills-grid">
                  {skills.map(skill => (
                    <label key={skill._id} className={`emp-post-job-skill-chip${formData.requiredSkills.includes(skill._id) ? ' selected' : ''}`}>
                      <input type="checkbox" value={skill._id} checked={formData.requiredSkills.includes(skill._id)} onChange={handleSkillsChange} />
                      {skill.name}
                    </label>
                  ))}
                </div>
              </div> */}
            </div>
            {/* <div className="emp-post-job-field">
              <label className="emp-post-job-label">Optional Skills</label>
              {employerSkills.length > 0 && (
                <div style={{marginBottom: 15}}>
                  <div style={{fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600}}>Available Skills:</div>
                  <div className="emp-post-job-skills-grid">
                    {employerSkills.map(skill => (
                      <label key={skill._id} className={`emp-post-job-skill-chip${formData.optionalSkills?.includes(skill._id) ? ' selected' : ''}`}>
                        <input type="checkbox" value={skill._id} checked={formData.optionalSkills?.includes(skill._id)} onChange={handleOptionalSkillsChange} />
                        <span>{skill.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div> */}
            <div className="emp-post-job-field">
              <label className="emp-post-job-label">Experience <span>*</span></label>
              <div className="emp-post-job-row">
                <div className="emp-post-job-field">
                  <label className="emp-post-job-label">Min</label>
                  <input type="text" name="experienceRequired.min" value={formData.experienceRequired?.min} onChange={handleNumericChange} inputMode="numeric" pattern="[0-9]*" className="emp-post-job-input" required />
                </div>
                <div className="emp-post-job-field">
                  <label className="emp-post-job-label">Max</label>
                  <input type="text" name="experienceRequired.max" value={formData.experienceRequired?.max} onChange={handleNumericChange} inputMode="numeric" pattern="[0-9]*" className="emp-post-job-input" required />
                </div>
              </div>
            </div>
            <div className="emp-post-job-field">
              <label className="emp-post-job-label">Education Details</label>
              <select name="educationRequired" value={formData.educationRequired} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                <option value="">Select Education Level</option>
                {educations.map(edu => <option key={edu._id} value={edu._id}>{edu.name}</option>)}
              </select>
            </div>
          </div>
        </div>



        {/* Salary */}
        <div className="emp-post-job-card">
          <h2 className="emp-post-job-card-title">
            <span className="material-symbols-outlined">currency_rupee</span>
            Salary
          </h2>
          <div className="emp-post-job-form">
            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Min Salary</label>
                <input type="number" name="salary.min" value={formData.salary.min} onChange={handleChange} min="0" className="emp-post-job-input" />
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Max Salary</label>
                <input type="number" name="salary.max" value={formData.salary.max} onChange={handleChange} min="0" className="emp-post-job-input" />
              </div>
            </div>
            <div className="emp-post-job-row">
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Currency</label>
                <select name="salary.currency" value={formData.salary.currency} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div className="emp-post-job-field">
                <label className="emp-post-job-label">Period</label>
                <select name="salary.period" value={formData.salary.period} onChange={handleChange} className="emp-post-job-select jl-filter-drop">
                  <option value="hourly">Per Hour</option>
                  <option value="daily">Per Day</option>
                  <option value="monthly">Per Month</option>
                  <option value="yearly">Per Year</option>
                </select>
              </div>
            </div>
            <div className="emp-post-job-row">
              <div className="emp-post-job-field" style={{ justifyContent: 'flex-start' }}>
                <label className="emp-post-job-checkbox-row">
                  <input type="checkbox" name="salary.negotiable" checked={formData.salary.negotiable} onChange={handleChange} />
                  Mark salary as Negotiable
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="emp-post-job-form-actions">
          <button type="button" className="emp-post-job-cancel-btn" onClick={() => navigate('/employer/my-jobs')} disabled={loading}>Cancel</button>
          <button type="button" className="emp-post-job-draft-btn" onClick={(e) => handleSubmit(e, 'draft')} disabled={loading}>
            <span className="material-symbols-outlined">save</span>
            {loading ? 'Saving…' : 'Save Draft'}
          </button>
          <button type="button" className="emp-post-job-preview-btn" onClick={() => setShowPreview(true)} disabled={loading}>
            <span className="material-symbols-outlined">visibility</span>
            Preview
          </button>
          <button type="button" className="emp-post-job-publish-btn" onClick={(e) => handleSubmit(e, 'active')} disabled={loading}>
            <span className="material-symbols-outlined">rocket_launch</span>
            {loading ? 'Publishing…' : 'Publish Job'}
          </button>
        </div>
      </form>

      {showPreview && (
        <div className="emp-post-job-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="emp-post-job-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="emp-post-job-preview-close" onClick={() => setShowPreview(false)}>×</button>

            <div className="emp-post-job-preview-hero">
              <h2>{formData.title || 'Job Title'}</h2>
              <p>{companies[0]?.name || 'Company'} • {[formData.location.city, formData.location.state, formData.location.country].filter(Boolean).join(', ')}</p>
              <div className="emp-post-job-preview-meta">
                {formData.workMode && <span><span className="material-symbols-outlined">business_center</span>{formData.workMode}</span>}
              </div>
            </div>

            <div className="emp-post-job-preview-body">
              <div className="emp-post-job-preview-summary">
                <div className="emp-post-job-preview-summary-item"><strong>Salary</strong>{formData.salary.currency} {formData.salary.min?.toLocaleString()} – {formData.salary.max?.toLocaleString()} / {formData.salary.period}{formData.salary.negotiable ? ' (Negotiable)' : ''}</div>
                <div className="emp-post-job-preview-summary-item"><strong>Experience</strong>{formData.experienceRequired.min} – {formData.experienceRequired.max} Years</div>
                <div className="emp-post-job-preview-summary-item"><strong>Positions</strong>{formData.numberOfPositions}</div>
                {formData.applicationDeadline && <div className="emp-post-job-preview-summary-item"><strong>Deadline</strong>{new Date(formData.applicationDeadline).toLocaleDateString(undefined, {day:'numeric',month:'short',year:'numeric'})}</div>}
                {formData.jobType && <div className="emp-post-job-preview-summary-item"><strong>Job Type</strong>{jobTypes.find(j => j._id === formData.jobType)?.name || 'N/A'}</div>}
                {formData.shiftType && <div className="emp-post-job-preview-summary-item"><strong>Shift</strong>{shiftTypes.find(s => s._id === formData.shiftType)?.name || 'N/A'}</div>}
                {formData.educationRequired && <div className="emp-post-job-preview-summary-item"><strong>Education</strong>{educations.find(e => e._id === formData.educationRequired)?.name || 'N/A'}</div>}
              </div>

              {formData.description && <div className="emp-post-job-preview-section"><h3>Description</h3><p>{formData.description}</p></div>}
              {formData.responsibilities && <div className="emp-post-job-preview-section"><h3>Responsibilities</h3><p>{formData.responsibilities}</p></div>}
              {formData.requirements && <div className="emp-post-job-preview-section"><h3>Requirements</h3><p>{formData.requirements}</p></div>}

              {formData.requiredSkills.length > 0 && (
                <div className="emp-post-job-preview-section">
                  <h3>Required Skills</h3>
                  <div className="emp-post-job-preview-chips">
                    {formData.requiredSkills.map(id => {
                      const s = employerSkills.find(x => x._id === id);
                      return s ? <span key={id} className="emp-post-job-preview-chip">{s.name}</span> : null;
                    })}
                  </div>
                </div>
              )}
              {formData.benefits.length > 0 && (
                <div className="emp-post-job-preview-section">
                  <h3>Benefits</h3>
                  <div className="emp-post-job-preview-chips">
                    {formData.benefits.map(id => { const b = benefits.find(x => x._id === id); return b ? <span key={id} className="emp-post-job-preview-chip">{b.name}</span> : null; })}
                  </div>
                </div>
              )}

              <div className="emp-post-job-preview-section">
                <h3>How to Apply</h3>
                {formData.applicationMethod.type === 'internal' && <p>Apply directly on HiringOpen (Internal Application)</p>}
                {formData.applicationMethod.type === 'email' && <p>Send application to: <strong>{formData.applicationMethod.email || 'Not specified'}</strong></p>}
                {formData.applicationMethod.type === 'external' && <p>Apply via: <a href={formData.applicationMethod.externalUrl} target="_blank" rel="noopener noreferrer">{formData.applicationMethod.externalUrl || 'Not specified'}</a></p>}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PostJob;
